import axiosInstance from "./axiosInstance";
import logger from "../utils/logger";
import { extractErrorMessage } from "../utils/errorHandler";
import { CENTRAL_DB_BASE_URL } from "../config/centralDbRuntimeUrl";
import {
    Worker,
    WorkerSearchResponse,
    UpdateWorkerRequest,
    AddWorkerRequest,
    BulkUpdateWorkersRequest,
    BulkDeleteWorkersDto,
    WorkerSearchRequest
} from "../models/worker";

export const fetchWorkersRequest = async (params: WorkerSearchRequest): Promise<WorkerSearchResponse> => {
    try {
        console.log("Making workers API request with params:", params);
        const { page, limit, searchParam, ...filters } = params;
        const response = await axiosInstance.post<WorkerSearchResponse>(
            '/workers/search',
            filters,
            {
                params: { page, limit, search: searchParam }
            }
        );
        console.log("Workers API response:", response);
        return response.data;
    } catch (error: unknown) {
        console.error("Workers API error details:", error);
        logger.error("Error has occurred attempting to fetch the workers. Please try again or contact administration.");
        throw error;
    }
};

// Mirrors WorkersService.getCountsByStatus — unfolded per-status tab counts
// (T16 state machine) + the two overlay-toggle badges.
export interface WorkerCountsByStatus {
    incomplete: number;
    pendingDocuments: number;
    pendingApproval: number;
    candidate: number;
    placed: number;
    reservePool: number;
    all: number;
    rejectedBlocked: number;
    nonEu: number;
}

export interface WorkerCountsByAssignee {
    byUser: Record<string, number>;
    unassigned: number;
    total: number;
}

export const fetchWorkerCountsByStatus = async (filters: Record<string, any> = {}): Promise<WorkerCountsByStatus> => {
    const response = await axiosInstance.post<WorkerCountsByStatus>('/workers/counts/by-status', filters);
    return response.data;
};

export const fetchWorkerCountsByAssignee = async (filters: Record<string, any> = {}): Promise<WorkerCountsByAssignee> => {
    const response = await axiosInstance.post<WorkerCountsByAssignee>('/workers/counts/by-assignee', filters);
    return response.data;
};

export const fetchWorkerByIdRequest = async (workerId: string): Promise<Worker> => {
    try {
        console.log("Fetching worker by ID:", workerId);
        const response = await axiosInstance.get<Worker>(`/workers/${workerId}`);
        console.log("Worker API response:", response);
        return response.data;
    } catch (error: unknown) {
        console.error("Worker API error details:", error);
        logger.error("Error has occurred attempting to fetch the worker. Please try again or contact administration.");
        throw error;
    }
};

export interface WorkerAuditEntry {
    id: string;
    field: string;
    oldValue: unknown | null;
    newValue: unknown | null;
    actorType: 'WORKER' | 'USER' | 'AI' | 'SYSTEM' | 'CV';
    actorId?: string | null;
    actorLabel?: string | null;
    metadata?: { source?: string; valueRedacted?: boolean } | null;
    createdAt: string;
}

export const fetchWorkerHistory = async (workerId: string): Promise<WorkerAuditEntry[]> => {
    const response = await axiosInstance.get<WorkerAuditEntry[]>(
        `/workers/${encodeURIComponent(workerId)}/history`,
    );
    return response.data;
};

export const updateWorkerRequest = async (requestBody: UpdateWorkerRequest): Promise<Worker> => {
    try {
        const { workerId, ...updateData } = requestBody;
        const response = await axiosInstance.put<Worker>(`/workers/${workerId}`, updateData);
        return response.data;
    } catch (error: unknown) {
        console.error(error);
        logger.error("Error has occurred attempting to update the worker. Please try again or contact administration.");
        throw error;
    }
};

export const createWorkerRequest = async (requestBody: AddWorkerRequest): Promise<Worker> => {
    try {
        const response = await axiosInstance.post<Worker>('/workers', requestBody);
        return response.data;
    } catch (error: unknown) {
        console.error(error);
        logger.error("Error has occurred attempting to create the worker. Please try again or contact administration.");
        throw error;
    }
};

export const deleteWorkerRequest = async (workerId: string) => {
    try {
        await axiosInstance.delete(`/workers/${workerId}`);
    } catch (error) {
        logger.error("An error occurred attempting to remove the worker. Please try again or contact administration.");
        console.error("❌ Error deleting worker", error);
        throw error;
    }
}

export const bulkUpdateWorkersRequest = async (requestBody: BulkUpdateWorkersRequest): Promise<Worker> => {
    try {
        const response = await axiosInstance.put<Worker>('/workers-bulk/update', requestBody);
        return response.data;
    } catch (error: unknown) {
        console.error(error);
        logger.error("Error has occurred attempting to bulk update workers. Please try again or contact administration.");
        throw error;
    }
};

export const bulkDeleteWorkersRequest = async (requestBody: BulkDeleteWorkersDto): Promise<void> => {
    try {
        await axiosInstance.delete('/workers-bulk/delete', { data: requestBody });
    } catch (error: unknown) {
        console.error(error);
        logger.error("Error has occurred attempting to bulk delete workers. Please try again or contact administration.");
        throw error;
    }
};

export async function downloadBulkWorkerPDFs(
    workerIds: string[],
    includePersonalInfo: boolean = true
): Promise<void> {
    try {
        const response = await axiosInstance.post('/workers/bulk-pdf', {
            workerIds,
            includePersonalInfo
        }, {
            responseType: 'blob',
            headers: {
                'Accept': 'application/zip'
            },
            // Longer timeout for bulk operations
            timeout: 300000 // 5 minutes
        });
        
        // Create a blob URL from the response
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `worker-cvs-${Date.now()}.zip`;
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }, 100);
    } catch (error) {
        console.error('Error downloading bulk PDFs:', error);
        throw error;
    }
}

export async function downloadWorkerPDF(
    workerId: string, 
    includePersonalInfo: boolean = true,
    sections?: { id: string; visible: boolean }[],
    items?: { id: string; visible: boolean }[]
): Promise<string | null> {
    try {
        const response = await axiosInstance.post(`/workers/${workerId}/pdf`, {
            includePersonalInfo,
            sections,
            items
        }, {
            responseType: 'blob',
            headers: {
                'Accept': 'application/pdf'
            }
        });
        
        // Create a blob URL from the response
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `worker-${workerId}.pdf`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return url;
    } catch (error: unknown) {
        console.error('Error downloading PDF:', error);
        const errorMessage = await extractErrorMessage(error);
        throw new Error(errorMessage || 'Failed to download PDF');
    }
}

export async function getWorkerHTML(workerId: string, includePersonalInfo: boolean = true): Promise<string | null> {
    try {
        const response = await axiosInstance.get(`/workers/${workerId}/html`, {
            headers: {
                'Accept': 'text/html'
            },
            params: {
                includePersonalInfo,
            }
        });
        
        return response.data;
    } catch (error) {
        console.error('Error fetching HTML:', error);
        return null;
    }
}

export interface CVGenerationWarnings {
    storyFallback?: boolean;
    skillsFallback?: boolean;
    highlightsFallback?: boolean;
}

export interface CVGenerationResult {
    html: string;
    warnings?: CVGenerationWarnings;
}

export async function getWorkerHTMLWithConfig(
    workerId: string,
    config: {
        includePersonalInfo?: boolean;
        extraInstructions?: string;
        categoryToggles?: { personal: boolean; professional: boolean; other: boolean; professionContext: boolean };
        excludedQuestionIds?: number[];
        language?: string;
        contactInfo?: { name: string; email: string; phone: string };
        theme?: string;
    }
): Promise<CVGenerationResult | null> {
    try {
        const response = await axiosInstance.post(`/workers/${workerId}/html-with-config`, config);
        // Backend returns JSON with { html, warnings }
        if (typeof response.data === 'object' && response.data.html) {
            return response.data;
        }
        // Backwards compat: if raw HTML string is returned
        if (typeof response.data === 'string') {
            return { html: response.data };
        }
        return null;
    } catch (error) {
        console.error('Error fetching HTML with config:', error);
        return null;
    }
}

export async function generatePDFFromHTML(workerId: string, htmlContent: string): Promise<string | null> {
    try {
        const response = await axiosInstance.post(`/workers/${workerId}/pdf-from-html`, 
            { htmlContent },
            {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/pdf',
                    'Content-Type': 'application/json'
                }
            }
        );
        
        // Create a blob URL from the response
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `worker-${workerId}-edited.pdf`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return url;
    } catch (error: unknown) {
        console.error('Error generating PDF from HTML:', error);
        const errorMessage = await extractErrorMessage(error);
        console.error('Error message:', errorMessage);
        return null;
    }
}

// ============= AI AUTOFILL =============

export interface AiAutofillPayload {
    worker: Record<string, any>;
    memo?: string;
    professionKey?: string;
    uploadedDocuments?: Array<{
        content_base64: string;
        mime_type: string;
        label?: string;
    }>;
    easyflexDocumentNumbers?: number[];
    sessionLanguage?: string;
}

export interface AiSuggestionItem {
    field: string;
    label: string;
    suggestedValue: string;
    source: string;
    confidence?: number;
}

export interface AiAutofillResult {
    suggestions: AiSuggestionItem[];
    professionSuggestion?: {
        professionKey: string;
        professionName: string;
        confidence: number;
        reasoning: string;
    };
    errors: string[];
    processedBy: string;
}

export const requestAiAutofill = async (payload: AiAutofillPayload): Promise<AiAutofillResult> => {
    try {
        const response = await axiosInstance.post<AiAutofillResult>('/workers/ai-autofill', payload, {
            timeout: 90000, // 90 second timeout for full AI pipeline
        });
        return response.data;
    } catch (error: unknown) {
        console.error('AI autofill API error:', error);
        throw error;
    }
};

// ============= PUSH TO SALESLIST =============

export interface PushToSaleslistOptions {
    createOpportunity?: boolean;
    availableAt?: string;
}

export interface PushToSaleslistResult {
    status: string;
    data: {
        commandId: string;
        state: 'PENDING' | 'PROCESSING' | 'SENT' | 'FAILED';
        queued: boolean;
        retryable?: boolean;
        centralDbWorkerId?: string;
        opportunityId?: string;
        opportunityReferenceCode?: string;
        existingWorkerFound?: boolean;
        error?: string;
    };
    message: string;
}

export const pushWorkerToSaleslist = async (
    workerId: string,
    options: PushToSaleslistOptions,
    commandId: string,
): Promise<PushToSaleslistResult> => {
    try {
        const response = await axiosInstance.post<PushToSaleslistResult>(
            `/workers/${encodeURIComponent(workerId)}/push-to-saleslist`,
            options,
            { headers: { 'Idempotency-Key': commandId } },
        );
        return response.data;
    } catch (error: unknown) {
        console.error('Error pushing worker to saleslist:', error);
        logger.error('Error pushing worker to saleslist. Please try again or contact administration.');
        throw error;
    }
};

export async function fetchWorkerSaleslistPushStatus(
    workerId: string,
    commandId: string,
): Promise<PushToSaleslistResult['data']> {
    const response = await axiosInstance.get<PushToSaleslistResult['data']>(
        `/workers/${encodeURIComponent(workerId)}/saleslist-push-commands/${encodeURIComponent(commandId)}`,
    );
    return response.data;
}

export type WorkerCandidatePublicationState = 'PENDING' | 'PROCESSING' | 'SENT' | 'FAILED';

export interface WorkerCandidatePublicationStatus {
    id: string;
    state: WorkerCandidatePublicationState;
    attempts: number;
    retryable: boolean;
    failureType: string | null;
    updatedAt: string;
}

export async function fetchWorkerCandidatePublicationStatus(
    workerId: string,
): Promise<WorkerCandidatePublicationStatus | null> {
    const response = await axiosInstance.get<WorkerCandidatePublicationStatus | null>(
        `/workers/${encodeURIComponent(workerId)}/candidate-publication`,
    );
    return response.data;
}

export async function retryWorkerCandidatePublication(
    workerId: string,
): Promise<WorkerCandidatePublicationStatus> {
    const response = await axiosInstance.post<WorkerCandidatePublicationStatus>(
        `/workers/${encodeURIComponent(workerId)}/candidate-publication/retry`,
    );
    return response.data;
}

// ============= WORKER PLACEMENTS + PROJECTS (Central DB) =============

export interface PlacementsWithProjectsResponse {
    items: any[];
    total: number;
}

/**
 * Fetch placements for a worker enriched with linked Easyflex project details
 * from Central DB backend.
 *
 * Uses VITE_CENTRAL_DB_API_URL if provided; otherwise falls back to the main API base.
 */
export const fetchPlacementsWithProjects = async (
    workerId: string,
    options: { onlyActive?: boolean } = {}
): Promise<PlacementsWithProjectsResponse> => {
    const CENTRAL_DB_BASE = CENTRAL_DB_BASE_URL;
    const onlyActive = options.onlyActive ?? true;

    // Force DB-only placements (no SOAP) using view=bare
    const url = CENTRAL_DB_BASE
        ? `${CENTRAL_DB_BASE.replace(/\/$/, '')}/workers/${workerId}/placements`
        : `/workers/${workerId}/placements`;

    const response = await axiosInstance.get<PlacementsWithProjectsResponse>(url, {
        params: { onlyActive, view: 'bare' }
    });
    return response.data;
};

// ============= EASYFLEX WORKERS API =============

export type EasyflexAvailability = 'PLACED' | 'AVAILABLE' | 'INACTIVE';

/**
 * Granular status filter matching the recruiter badges 1:1. Active workers
 * split by placement (AVAILABLE / PLACED); the rest each pin one EasyFlex code.
 * Keep in sync with the backend `EasyflexStatusFilter` enum.
 */
export type EasyflexStatusFilter =
    | 'AVAILABLE'
    | 'PLACED'
    | 'RECENTLY_LEFT'
    | 'LEFT_SERVICE'
    | 'NEVER_PLACED';

/** Dropdown options for the granular status filter (label ↔ badge label). */
export const EASYFLEX_STATUS_FILTER_OPTIONS: { value: EasyflexStatusFilter; label: string }[] = [
    { value: 'AVAILABLE', label: 'Available' },
    { value: 'PLACED', label: 'Placed' },
    { value: 'RECENTLY_LEFT', label: 'Recently left' },
    { value: 'LEFT_SERVICE', label: 'Left service' },
    { value: 'NEVER_PLACED', label: 'Registered, never placed' },
];

export interface EasyFlexWorker {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    status: string;
    /** Raw EasyFlex flexwerkerstatus code (null when absent/non-numeric). */
    easyflexStatusCode?: number | null;
    /** Verbatim EasyFlex status label (e.g. "Actief"), decoded server-side. */
    easyflexStatusLabel?: string;
    /** One-sentence recruiter-facing explanation of the status (info tooltip). */
    easyflexStatusDescription?: string;
    /** Derived recruiter availability: active+unplaced / active+placed / inactive. */
    availability?: EasyflexAvailability;
    profession?: string;
    jobTitle?: string;
    skills?: string[];
    certifications?: string[];
    languages?: string[];
    yearsOfExperience?: number;
    createdAt: string;
    updatedAt: string;

    // Enriched personal fields
    nationality?: string;
    dateOfBirth?: string;
    gender?: string;
    postalAddress?: {
        street?: string;
        houseNumber?: string;
        addition?: string;
        postalCode?: string;
        city?: string;
        country?: string;
    };
    residentialAddress?: {
        street?: string;
        houseNumber?: string;
        addition?: string;
        postalCode?: string;
        city?: string;
        country?: string;
    };
    driverLicense?: {
        number?: string;
        validUntil?: string;
        categories?: Record<string, boolean>;
    };
    workPermit?: {
        type?: string;
        issuer?: string;
        issueDate?: string;
        number?: string;
        validUntil?: string;
    };
    emergencyContact?: {
        name?: string;
        relationship?: string;
        phoneNumber?: string;
        email?: string;
    };
    easyflexRegistrationNumber?: number;
    /** Sync-source organization ("Optima Uitzendburo" / "Optima Work") — the
     *  regnum's disambiguator. Store BOTH when linking a worker. */
    organizationName?: string;

    // EasyFlex specific fields
    hasActivePlacement: boolean;
    currentPlacement?: {
        id: string;
        title: string;
        organizationName: string;
        startDate: string;
        endDate?: string;
        status: string;
        functionName: string;
        hoursPerWeek?: number;
        clientName?: string;
        easyflexPlacementNumber?: number;
    };
    currentOccupation?: string;
    currentOccupationTranslated?: {
        optimaProfessionKey?: string;
        optimaProfessionName?: string;
        category?: string;
    };
    historicalOccupations: string[];
    translatedOccupations: Array<{
        easyflexFunctionName: string;
        optimaProfessionKey?: string;
        optimaProfessionName?: string;
        category?: string;
    }>;
    placements?: Array<{
        id: string;
        title: string;
        organizationName?: string;
        startDate: string;
        endDate?: string;
        functionName?: string;
        status: string;
        hoursPerWeek?: number;
        clientName?: string;
        easyflexPlacementNumber?: number;
        isActive?: boolean;
    }>;
    business?: {
        id: string;
        name: string;
        businessType: string;
    };
    totalWorkHours?: number;
}

export interface EasyFlexWorkersResponse {
    status: string;
    data: EasyFlexWorker[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    filters: {
        hasActivePlacement?: boolean;
        occupation?: string;
        profession?: string;
        category?: string;
        search?: string;
    };
    message: string;
}

export interface EasyFlexDocument {
    documentNumber: number;
    subject: string;
    fileName: string;
    mimeType: string;
    date: string;
    organization: string;
    herkomst: number | null;
    typeVast: number | null;
    typeOvk: number | null;
    typeCustom: number | null;
    hidden: boolean;
    belangrijk: boolean;
    publiceren: number | null;
    informatie: string;
    reden: string;
    gewijzigd: string;
    geldigTot: string;
    contractnummer: number | null;
    plaatsingnummer: number | null;
    flexwerkernummer: number | null;
    relatienummer: number | null;
    documentmapFlexwerker: number | null;
    documentmapRelatie: number | null;
    verwijderd: number | null;
    dhStatus: number | null;
}

export interface EasyFlexWorkerFilters {
    page?: number;
    limit?: number;
    hasActivePlacement?: boolean;
    occupation?: string;
    profession?: string;
    category?: string;
    search?: string;
    sortBy?: 'name' | 'status' | 'occupation' | 'placement' | 'business' | 'experience' | 'hours' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
    availability?: EasyflexAvailability;
    /** Granular status filter matching the recruiter badges. */
    easyflexStatus?: EasyflexStatusFilter;
}

export type EasyflexFreshness = 'cached' | 'refresh_async' | 'refresh_sync';

export type EasyflexDataSyncStartResponse = {
    syncRunId?: string;
    key?: string;
    organizations?: string[];
};

export type EasyflexDataSyncJobStatus = {
    id: string | number;
    state: string;
    progress: unknown;
    attemptsMade: number;
    failedReason?: string;
    finishedOn?: number;
    processedOn?: number;
    data?: unknown;
};

export type EasyflexDataSyncBySyncRunIdResponse = {
    job: EasyflexDataSyncJobStatus;
};

export const startEasyflexDataSyncJob = async (key: string, options?: { organizations?: string[] }): Promise<EasyflexDataSyncStartResponse> => {
    const safeKey = String(key || '').trim();
    if (!safeKey) throw new Error('sync key is required');

    const orgs = Array.isArray(options?.organizations) ? options.organizations.filter(Boolean) : [];
    const response = await axiosInstance.post(`/workers/easyflex/syncs/${encodeURIComponent(safeKey)}/start`, orgs.length ? { organizations: orgs } : {});
    return response.data || {};
};

export const getEasyflexDataSyncBySyncRunId = async (syncRunId: string): Promise<EasyflexDataSyncBySyncRunIdResponse> => {
    const id = String(syncRunId || '').trim();
    if (!id) throw new Error('syncRunId is required');
    const response = await axiosInstance.get<EasyflexDataSyncBySyncRunIdResponse>(`/workers/easyflex/syncs/by-sync-run/${encodeURIComponent(id)}`);
    return response.data;
};

/**
 * Fetch EasyFlex workers for recruitment
 */
export const fetchEasyFlexWorkers = async (filters: EasyFlexWorkerFilters = {}): Promise<EasyFlexWorkersResponse> => {
    try {
        console.log("Fetching EasyFlex workers with filters:", filters);
        
        const response = await axiosInstance.get<EasyFlexWorkersResponse>('/workers/easyflex', {
            params: filters
        });
        
        console.log(`Successfully fetched ${response.data.data.length} EasyFlex workers`);
        return response.data;
        
    } catch (error: unknown) {
        console.error("EasyFlex workers API error:", error);
        logger.error("Error occurred while fetching EasyFlex workers. Please try again or contact administration.");
        throw error;
    }
};

/**
 * Fetch all EasyFlex documents for a worker by registration number
 */
export const fetchWorkerDocuments = async (registrationNumber: number): Promise<EasyFlexDocument[]> => {
    try {
        const response = await axiosInstance.get<{
            status: string;
            data: EasyFlexDocument[];
        }>(`/workers/easyflex/workers/${registrationNumber}/documents`);

        return response.data.data || [];
    } catch (error: unknown) {
        console.error(`Failed to fetch documents for worker ${registrationNumber}:`, error);
        return [];
    }
};

/**
 * Cached EasyFlex documents for a worker, sourced from central-db's
 * `easyflex_documents` mirror. Includes LLM-classified metadata
 * (`inferredDocType`, `summary`, `inferredValidUntil`, etc.) that the
 * live-proxy variant above doesn't have.
 *
 * Use this for read-heavy paths like the WorkerDetail Documents tab.
 * For the import wizard's fresh-data needs, keep using `fetchWorkerDocuments`
 * — that hits the live-proxy and bypasses the mirror.
 */
export interface CachedEasyFlexDocument {
    id: string;
    organizationName: string;
    easyflexDocumentNumber: number;
    workerRegistrationNumber: number;
    subject: string | null;
    fileName: string | null;
    mimeType: string | null;
    documentDate: string | null;
    validUntil: string | null;
    informatie: string | null;
    reden: string | null;
    gewijzigdAt: string | null;
    herkomst: number | null;
    typeVast: number | null;
    typeOvk: number | null;
    typeCustom: number | null;
    hidden: boolean;
    belangrijk: boolean;
    publiceren: number | null;
    contractNumber: number | null;
    placementNumber: number | null;
    flexwerkerNumber: number | null;
    relatieNumber: number | null;
    documentmapFlexwerker: number | null;
    documentmapRelatie: number | null;
    verwijderd: number | null;
    dhStatus: number | null;
    inferredDocType: string | null;
    inferredValidUntil: string | null;
    summary: string | null;
    classifyConfidence: number | null;
    relevantToRecruitment: boolean | null;
    classifyModel: string | null;
    classifiedAt: string | null;
    fetchedAt: string | null;
    fingerprint: string;
}

export interface CachedEasyFlexDocumentsFilters {
    organization?: string;
    inferredDocType?: string;
    validOnly?: boolean;
    includeHidden?: boolean;
    onlyRelevantToRecruitment?: boolean;
}

export const fetchCachedEasyflexDocuments = async (
    registrationNumber: number,
    filters: CachedEasyFlexDocumentsFilters = {},
): Promise<CachedEasyFlexDocument[]> => {
    try {
        const params: Record<string, string> = {};
        if (filters.organization) params.organization = filters.organization;
        if (filters.inferredDocType) params.inferredDocType = filters.inferredDocType;
        if (filters.validOnly) params.validOnly = 'true';
        if (filters.includeHidden) params.includeHidden = 'true';
        if (filters.onlyRelevantToRecruitment) params.onlyRelevantToRecruitment = 'true';

        const response = await axiosInstance.get<{
            status: string;
            data: CachedEasyFlexDocument[];
        }>(`/workers/easyflex/workers/${registrationNumber}/documents/cached`, { params });

        return response.data.data || [];
    } catch (error: unknown) {
        console.error(`Failed to fetch cached documents for worker ${registrationNumber}:`, error);
        return [];
    }
};

/**
 * Get the URL to view/download a specific EasyFlex document.
 * Returns the URL through the central-db proxy (which streams the binary).
 */
export const downloadEasyFlexDocument = async (documentNumber: number, filename: string): Promise<void> => {
    const response = await axiosInstance.get(`/workers/easyflex/documents/${documentNumber}/content`, {
        params: { filename },
        responseType: 'blob',
    });
    const blob = response.data as Blob;
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
};

export interface BulkImportCandidate extends EasyFlexWorker {
    alreadyImported: boolean;
    importedWorkerId?: string | null;
}

export interface BulkImportCandidatesResponse {
    status: string;
    data: BulkImportCandidate[];
    pagination: EasyFlexWorkersResponse['pagination'];
}

export interface BulkImportItemResult {
    easyflexRegistrationNumber: number;
    status: 'created' | 'already-imported' | 'error';
    workerId?: string;
    error?: string;
}

export interface BulkImportResponse {
    status: string;
    results: BulkImportItemResult[];
    summary: { created: number; alreadyImported: number; errors: number };
}

export interface BulkCreateSessionsEnqueueResponse {
    status: string;
    syncRunId: string;
    queued: number;
    skipped: string[];
}

export type BulkSessionJobStatus =
    | 'QUEUED'
    | 'IN_PROGRESS'
    | 'AWAITING_OPTIMA_AI'
    | 'COMPLETED'
    | 'ERROR';

export interface BulkSessionRunStatusItem {
    workerId: string;
    easyflexRegistrationNumber: number | null;
    status: BulkSessionJobStatus;
    strategy: 'CV_SYNC' | 'EASYFLEX_DOCS_ASYNC' | 'PROFILE_ONLY_ASYNC' | null;
    sessionId: number | null;
    suggestionsApplied: number;
    errorMessage: string | null;
    enqueuedAt: string | null;
    startedAt: string | null;
    completedAt: string | null;
}

export interface BulkSessionRunStatusResponse {
    status: string;
    syncRunId: string;
    counts: Record<BulkSessionJobStatus, number>;
    items: BulkSessionRunStatusItem[];
}

export const fetchBulkImportCandidates = async (
    filters: EasyFlexWorkerFilters = {},
): Promise<BulkImportCandidatesResponse> => {
    const response = await axiosInstance.get<BulkImportCandidatesResponse>(
        '/workers/easyflex/bulk-import/candidates',
        { params: filters },
    );
    return response.data;
};

export const bulkImportEasyflexWorkers = async (workers: EasyFlexWorker[]): Promise<BulkImportResponse> => {
    const response = await axiosInstance.post<BulkImportResponse>(
        '/workers/easyflex/bulk-import',
        { workers },
    );
    return response.data;
};

export interface PendingSessionWorker {
    workerId: string;
    firstName: string | null;
    lastName: string | null;
    emailAddress: string | null;
    phoneNumber: string | null;
    easyflexRegistrationNumber: number | null;
    importedAt: string;
}

export interface PendingSessionsResponse {
    status: string;
    data: PendingSessionWorker[];
    total: number;
}

export const fetchImportedPendingSessions = async (limit = 200): Promise<PendingSessionsResponse> => {
    const response = await axiosInstance.get<PendingSessionsResponse>(
        '/workers/imported-pending-sessions',
        { params: { limit } },
    );
    return response.data;
};

export const enqueueBulkCreateSessions = async (
    workerIds: string[],
): Promise<BulkCreateSessionsEnqueueResponse> => {
    const response = await axiosInstance.post<BulkCreateSessionsEnqueueResponse>(
        '/workers/bulk-create-sessions',
        { workerIds },
    );
    return response.data;
};

export const getBulkCreateSessionsStatus = async (
    syncRunId: string,
): Promise<BulkSessionRunStatusResponse> => {
    const response = await axiosInstance.get<BulkSessionRunStatusResponse>(
        `/workers/bulk-create-sessions/${encodeURIComponent(syncRunId)}`,
    );
    return response.data;
};

/**
 * Fetch specific EasyFlex worker by ID
 */
/**
 * Shared read layer. Fetches the EasyFlex profile for a given
 * regnum from optima-backend's `/workers/easyflex/profile/by-regnum/:regnum`
 * (which proxies central-db's for-recruitment endpoint). Consumers should
 * prefer the `useUnifiedEasyflexProfile` hook — it wraps this with
 * request dedup + a short-lived cache.
 */
/**
 * Import-wizard duplicate guard: is this EasyFlex profile already linked to
 * a local worker? Returns the existing worker's identity when it is.
 */
export interface EasyflexLinkedWorker {
    easyflexRegistrationNumber: number | string | null;
    easyflexOrganization: string | null;
    workerId: string;
    firstName: string | null;
    lastName: string | null;
}

/** Bulk "already imported" lookup for the EasyFlex tab. */
export const checkEasyflexLinksBulk = async (
    regnums: (number | string)[],
): Promise<EasyflexLinkedWorker[]> => {
    if (!regnums.length) return [];
    const response = await axiosInstance.post<{ linked: EasyflexLinkedWorker[] }>(
        '/workers/easyflex/link-check-bulk',
        { regnums },
    );
    return response.data.linked ?? [];
};

export const checkEasyflexLink = async (
    regnum: number | string,
    organization?: string | null,
): Promise<{ linked: boolean; worker?: { workerId: string; firstName?: string | null; lastName?: string | null; easyflexOrganization?: string | null } }> => {
    const response = await axiosInstance.get<{ linked: boolean; worker?: { workerId: string; firstName?: string | null; lastName?: string | null; easyflexOrganization?: string | null } }>(
        '/workers/easyflex/link-check',
        { params: { regnum: String(regnum), ...(organization ? { organization } : {}) } },
    );
    return response.data;
};

export const fetchEasyflexProfileByRegnum = async (
    regnum: number | string,
    organization?: string | null,
): Promise<EasyFlexWorker> => {
    // organization disambiguates regnum collisions across the two orgs —
    // pass the worker's `easyflexOrganization` whenever it's known.
    const response = await axiosInstance.get<{ status: string; data: EasyFlexWorker }>(
        `/workers/easyflex/profile/by-regnum/${encodeURIComponent(String(regnum))}`,
        { params: organization ? { organization } : undefined },
    );
    return response.data.data;
};

export const fetchEasyFlexWorkerById = async (id: string): Promise<EasyFlexWorker> => {
    try {
        console.log(`Fetching EasyFlex worker ${id}`);
        
        const response = await axiosInstance.get<{
            status: string;
            data: EasyFlexWorker;
            message: string;
        }>(`/workers/easyflex/${id}`);
        
        console.log(`Successfully fetched EasyFlex worker ${id}`);
        return response.data.data;
        
    } catch (error: unknown) {
        console.error(`EasyFlex worker ${id} API error:`, error);
        logger.error("Error occurred while fetching EasyFlex worker. Please try again or contact administration.");
        throw error;
    }
};

// the local EasyFlex mirror (fetch/save/update/delete/pull worker)
// was removed. EasyFlex profile reads now go through
// `fetchEasyflexProfileByRegnum` / `useUnifiedEasyflexProfile`; edits happen
// in EasyFlex and central-db picks them up on sync.

export const uploadWorkerFileToEasyFlex = async (workerId: string, fileId: number): Promise<any> => {
    try {
        const response = await axiosInstance.post(`/workers/${workerId}/easyflex/files/${fileId}/upload`, undefined, {
            timeout: 60000,
        });
        return response.data?.data ?? response.data;
    } catch (error: unknown) {
        console.error(`Upload worker file to EasyFlex API error (workerId=${workerId}, fileId=${fileId}):`, error);
        const errorMessage = await extractErrorMessage(error);
        throw new Error(errorMessage || 'Failed to upload document to EasyFlex');
    }
};
// ============= EASYFLEX ORGANIZATION DATA API =============

export interface EasyFlexOrganization {
    id: number;
    name: string;
    code?: string;
    email?: string;
    address?: string;
    city?: string;
    description?: string;
}

export interface EasyFlexOrganizationsResponse {
    status: string;
    data: EasyFlexOrganization[];
    message: string;
}

/**
 * Fetch EasyFlex inschrijvers (registration officers)
 */
export const fetchEasyFlexInschrijvers = async (): Promise<EasyFlexOrganization[]> => {
    try {
        console.log("Fetching EasyFlex inschrijvers");
        
        const response = await axiosInstance.get<EasyFlexOrganizationsResponse>('/workers/easyflex/organizations/inschrijvers');
        
        // Handle response structure
        const data = response.data.data || [];
        console.log(`Successfully fetched ${Array.isArray(data) ? data.length : 0} inschrijvers`);
        return Array.isArray(data) ? data : [];
        
    } catch (error: unknown) {
        console.error("EasyFlex inschrijvers API error:", error);
        logger.error("Error occurred while fetching EasyFlex inschrijvers. Please try again or contact administration.");
        return []; // Return empty array instead of throwing error
    }
};

/**
 * Fetch EasyFlex relatiebeheerders (account managers)
 */
export const fetchEasyFlexRelatiebeheerders = async (): Promise<EasyFlexOrganization[]> => {
    try {
        console.log("Fetching EasyFlex relatiebeheerders");
        
        const response = await axiosInstance.get<EasyFlexOrganizationsResponse>('/workers/easyflex/organizations/relatiebeheerders');
        
        // Handle response structure
        const data = response.data.data || [];
        console.log(`Successfully fetched ${Array.isArray(data) ? data.length : 0} relatiebeheerders`);
        return Array.isArray(data) ? data : [];
        
    } catch (error: unknown) {
        console.error("EasyFlex relatiebeheerders API error:", error);
        logger.error("Error occurred while fetching EasyFlex relatiebeheerders. Please try again or contact administration.");
        return []; // Return empty array instead of throwing error
    }
};

/**
 * Fetch EasyFlex locations
 */
export const fetchEasyFlexLocaties = async (): Promise<EasyFlexOrganization[]> => {
    try {
        console.log("Fetching EasyFlex locations");
        
        const response = await axiosInstance.get<EasyFlexOrganizationsResponse>('/workers/easyflex/organizations/locaties');
        
        // Handle response structure
        const data = response.data.data || [];
        console.log(`Successfully fetched ${Array.isArray(data) ? data.length : 0} locations`);
        return Array.isArray(data) ? data : [];
        
    } catch (error: unknown) {
        console.error("EasyFlex locations API error:", error);
        logger.error("Error occurred while fetching EasyFlex locations. Please try again or contact administration.");
        return []; // Return empty array instead of throwing error
    }
};

/**
 * Fetch EasyFlex business units
 */
export const fetchEasyFlexBusinessUnits = async (): Promise<EasyFlexOrganization[]> => {
    try {
        console.log("Fetching EasyFlex business units");
        
        const response = await axiosInstance.get<EasyFlexOrganizationsResponse>('/workers/easyflex/organizations/business-units');
        
        // Handle response structure
        const data = response.data.data || [];
        console.log(`Successfully fetched ${Array.isArray(data) ? data.length : 0} business units`);
        return Array.isArray(data) ? data : [];
        
    } catch (error: unknown) {
        console.error("EasyFlex business units API error:", error);
        logger.error("Error occurred while fetching EasyFlex business units. Please try again or contact administration.");
        return []; // Return empty array instead of throwing error
    }
};

/**
 * Apply a curated subset of CV-parsed answers to a worker (recruiter action
 * from the dashboard review modal). The backend writes directly to the
 * worker entity + session_questions; no WhatsApp / FSM side effects.
 * `arrayMode` controls how `work_experiences`, `education`, etc. merge with
 * existing entries — default 'append' is safer than 'replace'.
 */
export const applyParsedCv = async (
    workerId: string,
    body: { parsedAnswers: Record<string, any>; arrayMode?: 'append' | 'replace' },
): Promise<{ applied: number; skipped: string[] }> => {
    const response = await axiosInstance.post<{ applied: number; skipped: string[] }>(
        `/workers/${encodeURIComponent(workerId)}/apply-parsed-cv`,
        body,
    );
    return response.data;
};

// ─── NAW document API ─────────────────────────────────────────────────
//
// Three endpoints back the NawDocumentSection on the worker detail page:
//   readiness → drives the disabled Generate button + info-icon tooltip
//   generate  → renders + persists the PDF; returns fileId for download
//   latest    → fetches the most recent generated NAW for the download link

export interface NawReadiness {
    ready: boolean;
    /** Dutch field labels for missing required fields. Surfaced verbatim in the
     *  tooltip when the Generate button is disabled. */
    missing: string[];
}

export interface NawGenerateResult {
    fileId: number;
    fileName: string;
    readiness: NawReadiness;
}

export interface NawLatest {
    fileId: number;
    fileName: string;
    createdAt: string;
}

export const fetchNawReadiness = async (workerId: string): Promise<NawReadiness> => {
    const response = await axiosInstance.get<NawReadiness>(
        `/workers/${encodeURIComponent(workerId)}/naw-document/readiness`,
    );
    return response.data;
};

export const generateNawDocument = async (
    workerId: string,
    options: { force?: boolean; attachVcaCertificate?: boolean } = {},
): Promise<NawGenerateResult> => {
    const response = await axiosInstance.post<NawGenerateResult>(
        `/workers/${encodeURIComponent(workerId)}/naw-document/generate`,
        options,
    );
    return response.data;
};

export const fetchLatestNawDocument = async (workerId: string): Promise<NawLatest | null> => {
    const response = await axiosInstance.get<NawLatest | null>(
        `/workers/${encodeURIComponent(workerId)}/naw-document/latest`,
    );
    return response.data;
};
